import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaEnvelope, FaEye, FaSyncAlt } from "react-icons/fa";
import { emailLogsService } from "../../services/emailLogsService";
import { showToast, getErrorMessage } from "../../utils/toast";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const statusVariant = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "sent") return "success";
  if (normalized === "failed") return "danger";
  if (normalized === "partial") return "warning";
  if (normalized === "sending" || normalized === "processing") return "info";
  return "secondary";
};

const looksLikeHtml = (value) =>
  /<\/?[a-z][\s\S]*>/i.test(String(value || "").trim());

const EmailLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadLogs = useCallback(async (targetPage = page) => {
    try {
      setLoading(true);
      setError("");
      const response = await emailLogsService.getLogs({
        page: targetPage,
        limit: 20,
      });
      setLogs(response.logs || []);
      setPage(response.currentPage || targetPage);
      setTotalPages(Math.max(1, response.totalPages || 1));
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      console.error("Failed to load email logs:", err);
      setError("Failed to load email logs");
      showToast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadLogs(1);
    // Initial fetch only; subsequent pages use explicit handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLogs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return logs.filter((log) => {
      const recipients = asArray(log.recipient_emails).join(" ");
      const matchesSearch =
        !needle ||
        String(log.subject || "").toLowerCase().includes(needle) ||
        recipients.toLowerCase().includes(needle);
      const matchesStatus =
        status === "all" ||
        String(log.status || "").toLowerCase() === status.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [logs, search, status]);

  const viewDetails = async (log) => {
    try {
      setDetailsLoading(true);
      const response = await emailLogsService.getLogDetails(log.id);
      setSelectedLog(response.log || log);
    } catch (err) {
      showToast.error("Failed to load email details: " + getErrorMessage(err));
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-2">
            <FaEnvelope className="me-2" />
            Email Logs
          </h2>
          <p className="text-muted mb-0">
            Track sent, failed, and processing emails ({totalCount} total).
          </p>
        </div>
        <Button
          variant="outline-primary"
          onClick={() => loadLogs(page)}
          disabled={loading}
        >
          <FaSyncAlt className="me-2" />
          Refresh
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex gap-3 flex-wrap">
            <Form.Control
              style={{ maxWidth: 360 }}
              placeholder="Search subject or recipient..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Form.Select
              style={{ maxWidth: 200 }}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
              <option value="processing">Processing</option>
              <option value="sending">Sending</option>
            </Form.Select>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subject</th>
                  <th>Recipients</th>
                  <th>Status</th>
                  <th>Sent</th>
                  <th>Failed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No email logs found.
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => {
                    const recipients = asArray(log.recipient_emails);
                    return (
                      <tr key={log.id}>
                        <td>
                          {new Date(log.created_at).toLocaleString("en-IN")}
                        </td>
                        <td>{log.subject}</td>
                        <td title={recipients.join(", ")}>
                          {recipients[0] || "—"}
                          {recipients.length > 1 && ` +${recipients.length - 1}`}
                        </td>
                        <td>
                          <Badge bg={statusVariant(log.status)}>
                            {log.status || "Unknown"}
                          </Badge>
                        </td>
                        <td>{log.success_count || 0}</td>
                        <td>{log.failed_count || 0}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => viewDetails(log)}
                            disabled={detailsLoading}
                          >
                            <FaEye />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <Pagination className="justify-content-center mt-4">
          <Pagination.Prev
            disabled={page <= 1 || loading}
            onClick={() => loadLogs(page - 1)}
          />
          <Pagination.Item active>
            {page} / {totalPages}
          </Pagination.Item>
          <Pagination.Next
            disabled={page >= totalPages || loading}
            onClick={() => loadLogs(page + 1)}
          />
        </Pagination>
      )}

      <Modal
        show={Boolean(selectedLog)}
        onHide={() => setSelectedLog(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Email Log Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <>
              <p>
                <strong>Subject:</strong> {selectedLog.subject}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <Badge bg={statusVariant(selectedLog.status)}>
                  {selectedLog.status}
                </Badge>
              </p>
              <p>
                <strong>Recipients:</strong>{" "}
                {asArray(selectedLog.recipient_emails).join(", ") || "—"}
              </p>
              <p>
                <strong>Created:</strong>{" "}
                {new Date(selectedLog.created_at).toLocaleString("en-IN")}
              </p>
              {selectedLog.sent_at && (
                <p>
                  <strong>Completed:</strong>{" "}
                  {new Date(selectedLog.sent_at).toLocaleString("en-IN")}
                </p>
              )}
              <Form.Label>
                <strong>Email body</strong>
              </Form.Label>
              {looksLikeHtml(selectedLog.content) ? (
                <div
                  className="border rounded bg-white"
                  style={{ maxHeight: 420, overflow: "auto" }}
                >
                  <iframe
                    title="Email body preview"
                    srcDoc={selectedLog.content}
                    sandbox=""
                    style={{
                      width: "100%",
                      minHeight: 360,
                      border: 0,
                      background: "#fff",
                    }}
                  />
                </div>
              ) : (
                <div
                  className="border rounded p-3 bg-light"
                  style={{
                    whiteSpace: "pre-wrap",
                    maxHeight: 420,
                    overflow: "auto",
                  }}
                >
                  {selectedLog.content || "—"}
                </div>
              )}
              {asArray(selectedLog.failed_emails).length > 0 && (
                <Alert variant="danger" className="mt-3 mb-0">
                  <strong>Failed recipients:</strong>{" "}
                  {asArray(selectedLog.failed_emails).join(", ")}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EmailLogsTab;
